import React, { useState, useEffect } from "react";
import { Tooltip } from "@mui/material";
import InfoIcon from "@mui/icons-material/Info";
import { StyledButton, ButtonContent } from "./styled";

interface ButtonWithTooltipProps {
  /**
   * The text content of the button
   */
  children: React.ReactNode;
  /**
   * The tooltip text to display on hover
   */
  tooltipText?: string;
  /**
   * Whether the button is disabled
   */
  disabled?: boolean;
  /**
   * Click handler for the button
   */
  onClick?: () => void;
  /**
   * Custom styling for the button
   */
  customStyles?: React.CSSProperties;
  /**
   * CSS class name for the button
   */
  className?: string;
  /**
   * Test ID for testing purposes
   */
  "data-testid"?: string;
  /**
   * Button variant type
   */
  variant?: "primary" | "secondary" | "outline";
  /**
   * Button size
   */
  size?: "small" | "medium" | "large";
  /**
   * Whether to show the tooltip
   */
  showTooltip?: boolean;
  /**
   * Tooltip placement
   */
  tooltipPlacement?: "top" | "bottom" | "left" | "right" | "auto";
  /**
   * Whether to show info icon when tooltip is enabled
   */
  showInfoIcon?: boolean;
  /**
   * Custom icon size for the info icon
   */
  infoIconSize?: number;
  /**
   * Responsive tooltip placements for different screen sizes
   */
  responsivePlacements?: {
    mobile?: "top" | "bottom" | "left" | "right";
    tablet?: "top" | "bottom" | "left" | "right";
    desktop?: "top" | "bottom" | "left" | "right";
  };
  /**
   * Whether to disable tooltip on touch devices
   */
  disableOnTouch?: boolean;
}

/**
 * A customizable button component with integrated tooltip functionality.
 *
 * @description
 * ButtonWithTooltip provides a flexible button with optional tooltip support that appears only when hovering over the info icon.
 * The component supports multiple variants, sizes, responsive tooltip placement, and handles both desktop and touch device interactions.
 *
 * @example
 * ```tsx
 * // Basic usage with tooltip
 * <ButtonWithTooltip
 *   tooltipText="This button performs an action"
 *   onClick={handleClick}
 * >
 *   Click Me
 * </ButtonWithTooltip>
 *
 * // Outline variant with custom placement
 * <ButtonWithTooltip
 *   variant="outline"
 *   size="large"
 *   tooltipText="Advanced configuration options"
 *   tooltipPlacement="bottom"
 *   onClick={handleAdvancedClick}
 * >
 *   Advanced Settings
 * </ButtonWithTooltip>
 *
 * // Disabled state without tooltip
 * <ButtonWithTooltip
 *   disabled
 *   showTooltip={false}
 *   onClick={handleClick}
 * >
 *   Disabled Button
 * </ButtonWithTooltip>
 * ```
 *
 * @features
 * - Multiple button variants (primary, secondary, outline)
 * - Three size options (small, medium, large)
 * - Responsive tooltip placement with auto-adjustment
 * - Touch device detection and appropriate tooltip behavior
 * - Icon-only tooltip trigger (tooltip appears only when hovering over info icon)
 * - Boundary detection to prevent tooltip cutoff
 * - Customizable styling and theming
 * - Full accessibility support
 */
export const ButtonWithTooltip: React.FC<ButtonWithTooltipProps> = ({
  children,
  tooltipText,
  disabled = false,
  onClick,
  customStyles,
  className,
  "data-testid": dataTestId,
  variant = "primary",
  size = "medium",
  showTooltip = true,
  tooltipPlacement = "auto",
  showInfoIcon = true,
  infoIconSize = 14,
  responsivePlacements = {
    mobile: "bottom",
    tablet: "top",
    desktop: "top",
  },
  disableOnTouch = false,
}) => {
  const [currentPlacement, setCurrentPlacement] = useState<
    "top" | "bottom" | "left" | "right"
  >("top");
  const [isTouchDevice, setIsTouchDevice] = useState(false);
  const [screenSize, setScreenSize] = useState<"mobile" | "tablet" | "desktop">(
    "desktop"
  );

  useEffect(() => {
    const checkScreenSize = () => {
      const width = window.innerWidth;
      if (width < 768) {
        setScreenSize("mobile");
      } else if (width < 1024) {
        setScreenSize("tablet");
      } else {
        setScreenSize("desktop");
      }
    };

    const checkTouchDevice = () => {
      const hasTouch = "ontouchstart" in window || navigator.maxTouchPoints > 0;
      const isMobileUserAgent =
        /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(
          navigator.userAgent
        );
      const isSmallScreen = window.innerWidth < 1024; // Mobile or tablet size

      setIsTouchDevice(hasTouch && (isMobileUserAgent || isSmallScreen));
    };

    checkScreenSize();
    checkTouchDevice();

    window.addEventListener("resize", () => {
      checkScreenSize();
      checkTouchDevice();
    });
    return () => {
      window.removeEventListener("resize", checkScreenSize);
    };
  }, []);

  useEffect(() => {
    if (tooltipPlacement === "auto") {
      const placement = responsivePlacements[screenSize];
      if (placement) {
        setCurrentPlacement(placement);
      }
    } else {
      setCurrentPlacement(tooltipPlacement);
    }
  }, [screenSize, tooltipPlacement, responsivePlacements]);

  const shouldShowTooltip =
    showTooltip && tooltipText && !(disableOnTouch && isTouchDevice);

  const buttonElement = (
    <StyledButton
      $disabled={disabled}
      onClick={disabled ? undefined : onClick}
      style={customStyles}
      className={className}
      data-testid={dataTestId}
      $variant={variant}
      $size={size}
    >
      <ButtonContent>
        {children}
        {shouldShowTooltip && showInfoIcon && (
          <Tooltip
            title={tooltipText}
            arrow
            placement={currentPlacement}
            disableHoverListener={isTouchDevice && disableOnTouch}
            disableFocusListener={isTouchDevice && disableOnTouch}
            disableTouchListener={false}
            enterDelay={isTouchDevice ? 0 : 1000}
            leaveDelay={isTouchDevice ? 500 : 200}
            PopperProps={{
              modifiers: [
                {
                  name: "preventOverflow",
                  enabled: true,
                  options: {
                    altAxis: true,
                    altBoundary: true,
                    tether: true,
                    rootBoundary: "viewport",
                    padding: 8,
                  },
                },
              ],
            }}
          >
            <InfoIcon
              className="info-icon"
              sx={{
                fontSize: infoIconSize,
                color: disabled ? "#999" : "#9ca3afab",
                cursor: disabled ? "not-allowed" : "help",
                transition: "color 0.3s ease",
              }}
            />
          </Tooltip>
        )}
      </ButtonContent>
    </StyledButton>
  );

  return buttonElement;
};
